<template>
    <b-container fluid class="detail-page">

        <b-row class="">
            <b-col cols="12">
                <admin-page-header :title="modalTitle" description="Assessment 평가 및 서비스를 이용하는 기업 정보를 관리합니다."/>
            </b-col>
        </b-row>

        <div class="d-flex justify-content-between align-items-center mb-3">
            <div>
                <b-button variant="outline-secondary" @click="goToList">
                    <fa icon="arrow-left" class="mr-2"></fa>
                    {{  $t('목록으로') }}
                </b-button>
            </div>
            <div class="action-btns">
                <!-- [수정]과 [저장](submit)은 같은 자리라 key 로 다른 버튼임을 알린다. 같은 DOM 을 고쳐 쓰면
                     [수정] 클릭이 끝난 뒤 그 버튼이 submit 이 되어 수정 화면이 열리자마자 저장·검증이 돌았다 -->
                <template v-if="mode === 'detail'">
                    <b-button key="edit"
                              variant="outline-primary"
                              :disabled="!detailData.company_id"
                              @click="changeMode('edit')"
                    >
                        {{ $t('수정')}}
                    </b-button>
                </template>
                <template v-else-if="mode === 'add'">
                    <b-button variant="outline-primary"
                              form="company-form"
                              type="submit"
                              :disabled="saving"
                    >
                        <b-spinner v-if="saving" small class="mr-1"/>
                        {{ $t('저장')}}
                    </b-button>
                </template>
                <template v-else-if="mode === 'edit'">
                    <b-button key="save"
                              variant="outline-primary"
                              form="company-form"
                              type="submit"
                              :disabled="saving"
                    >
                        <b-spinner v-if="saving" small class="mr-1"/>
                        {{ $t('저장') }}
                    </b-button>
                    <b-button variant="outline-secondary"
                              :disabled="saving"
                              @click="cancelEdit"
                    >
                        {{ $t('취소') }}
                    </b-button>
                </template>
            </div>

        </div>

        <b-card no-body>

            <b-tabs class="default-tabs" :class="{'with-actions': mode !== 'add'}" content-class="mt-3"
                    :value="tabIndex" @input="onTabChange"
            >
                <b-tab :title="$t('기본 정보')">
                    <template v-if="loading">
                        <b-skeleton-img no-aspect class="empty-content"></b-skeleton-img>
                    </template>
                    <PageEmptyComponent v-else-if="loadError && mode !== 'add'" compact :error="loadError" @retry="fetchDetailData"/>
                    <template v-else>
                        <CompanyDetail v-if="mode === 'detail'" :detail-data="detailData"/>
                        <CompanyForm v-else ref="companyForm" :mode="mode" :detail-data="detailData"
                                     @updateDetailData="updateDetailData"
                                     @saving="v => saving = v"
                                     @dirty="v => formDirty = v"
                        />
                    </template>
                </b-tab>
                <b-tab v-if="mode === 'detail' && !loadError" :title="$t('소속 유저')">
                    <template v-if="loading">
                        <b-skeleton-img no-aspect class="empty-content"></b-skeleton-img>
                    </template>
                    <template v-else>
                        <CompanyMembers :members="detailData.member"
                                        :company="detailData"
                                        @updateMember="updateMember"
                        />
                    </template>
                </b-tab>
                <b-tab v-if="mode === 'detail' && !loadError" :title="$t('평가 참여 정보')">
                    <template v-if="loading">
                        <b-skeleton-img no-aspect class="empty-content"></b-skeleton-img>
                    </template>
                    <template v-else>
                        <AdminCompanyAssessmentInfo :selected_company="detailData" sync-route/>
                    </template>
                </b-tab>
                <b-tab v-if="mode === 'detail' && !loadError" :title="$t('연결 프로젝트')">
                    <template v-if="loading">
                        <b-skeleton-img no-aspect class="empty-content"></b-skeleton-img>
                    </template>
                    <template v-else>
                        <ConnectedProject :selected_company="detailData"/>
                    </template>
                </b-tab>
            </b-tabs>
        </b-card>
    </b-container>
</template>

<script>
import PageEmptyComponent from '@shared/components/admin/components/common/PageEmptyComponent.vue';
import {getErrorMessage} from '@core/utils/api/errorMessage';
import CompanyDetail from '@shared/components/admin/pages/Management/Company/_SubComponent/CompanyDetail.vue';
import CompanyForm from '@shared/components/admin/pages/Management/Company/_SubComponent/CompanyForm.vue';
import CompanyMembers from '@shared/components/admin/pages/Management/Company/_SubComponent/CompanyMembers.vue';
import AdminCompanyAssessmentInfo
    from '@shared/components/admin/pages/Management/Company/_SubComponent/AssessmentInfo.vue';
import ConnectedProject from '@shared/components/admin/pages/Management/Company/_SubComponent/ConnectedProject.vue';
import unsavedChangesGuard from '@shared/mixin/unsaved-changes-guard';

// ?tab= 값 (기본 정보는 키 생략). 탭 순서와 같다
const TAB_KEYS = ['info', 'members', 'assessments', 'projects'];

export default {
    name: 'CompanyManagementDetail',
    components: {
        PageEmptyComponent,
        ConnectedProject,
        AdminCompanyAssessmentInfo,
        CompanyMembers,
        CompanyForm,
        CompanyDetail,
    },
    mixins: [unsavedChangesGuard],
    data() {
        return {
            mode: 'detail',
            detailData: {id: null},
            loading: false,
            loadError: '',
            isFromExternal: false,
            saving: false,
            formDirty: false,
        };
    },
    computed: {
        hasUnsavedChanges() {
            return this.mode !== 'detail' && this.formDirty;
        },
        tabIndex() {
            return Math.max(0, TAB_KEYS.indexOf(this.$route.query.tab));
        },
        modalTitle() {
            if (this.mode === 'add') {
                return this.$t('기업 등록');
            } else if (this.mode === 'edit') {
                return this.$t('기업 수정');
            } else {
                return this.$t('기업 상세');
            }
        },
    },
    beforeRouteEnter(to, from, next) {
        next(vm => {
            if (from.name === null) {
                vm.isFromExternal = true;
            }
        });
    },
    created() {
        this.fetchDetailData();
    },
    methods: {
        changeMode(mode) {
            this.mode = mode;
            // 수정 화면은 기본 정보 탭 하나뿐이라, 끝나고 돌아올 때도 기본 정보 탭을 보이게 한다
            if (mode !== 'detail') this.replaceTabQuery(0);
        },
        async cancelEdit() {
            if (!(await this.confirmDiscardChanges())) return;
            this.changeMode('detail');
        },
        onTabChange(index) {
            // 수정 중·로드 실패 때는 탭이 하나뿐이라 b-tabs 가 0 을 돌려준다 — URL 을 바꾸지 않는다
            if (this.mode !== 'detail' || this.loadError) return;
            this.replaceTabQuery(index);
        },
        replaceTabQuery(index) {
            const {tab, ...rest} = this.$route.query;
            const next = index > 0 ? TAB_KEYS[index] : undefined;
            if (tab === next) return;
            this.$router.replace({query: next ? {...rest, tab: next} : rest}).catch(() => {});
        },
        goToList(){
            if (this.isFromExternal) {
                this.$router.replace('/admin/company');
            } else {
                this.$router.go(-1);
            }
        },
        async fetchDetailData() {
            try {
                this.loading = true;
                const company_id = this.$route.params.company_id;

                const _res_company_info = await this.$axios.get(`/admin/company/${company_id}/info`);
                const {company, member, projects} = _res_company_info.data;
                this.loadError = '';
                this.detailData = {
                    ...company, company_id: company_id,
                    member: member.map((item,index) => ({...item, index})), projects
                };
            } catch (e) {
                console.error(e);
                this.detailData = {id: null};
                if (e.response && e.response.status === 404) {
                    this.$EventBus.$emit('toast-failure', this.$t('기업 상세'), '존재하지 않는 기업입니다. 목록으로 돌아갑니다.');
                    this.goToList();
                } else {
                    this.loadError = getErrorMessage(e, '기업 정보를 불러오지 못했습니다.');
                }
            } finally {
                this.loading = false;
            }
        },
        updateMember(updatedUser){
            const userId = updatedUser.user_id || updatedUser.id;
            const idx = this.detailData.member.findIndex((item) => {
                return item.user_id === userId;
            });
            if(idx > -1){
                this.$set(this.detailData.member, idx, updatedUser);
            }
            else if(userId != null){
                this.detailData.member = this.detailData.member.concat([updatedUser]);
            }
        },
        async updateDetailData(detailData){
            this.detailData = {
                ...this.detailData,
                ...detailData,
            };
            if(this.mode === 'edit'){
                this.changeMode('detail');
            }
        }
    },
};
</script>

<style scoped lang="scss">

</style>
